import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import { browserHistory } from 'react-router';
import { Row, Col, Panel, Well, DropdownButton, Glyphicon, MenuItem, ButtonGroup, OverlayTrigger, Tooltip } from 'react-bootstrap';
import windowSize from 'react-window-size';
import request from 'axios';
import { getClientAuthHeader } from '../../client/token';
import Table, { getSquareColorTdProps } from './common/Table';
import SearchForm, { navigateToCases } from './common/SearchForm';
import ConfirmDialog from './common/ConfirmDialog';
import EditTableHeaders from './common/EditTableHeaders';
import TaskTable from './common/TaskTable';
import Button from './common/Button';
import CheckBox from './common/CheckBox';
import ImportDialog from './common/ImportDialog';
import CaseQuickActionDialogs, { QuickActionsMenu } from './common/CaseQuickActions';
import Cards from './Cards';
import { findBootstrapEnvironment, filteredRoute, downloadCsv, navigateToCase, loadAction } from './../helpers/utils';
import { getCaseStateColor } from './../helpers/colors';
import { casesLoadAction,
         casesPagingAction,
         caseSetServerError,
         caseEditIdAction,
         casesUpdateColumnsAction,
         casesDeleteAction,
         casesSetStatesAction
} from '../actions/caseActions';


class Cases extends PureComponent {
    constructor(props) {
        super(props);
        this.onColumnResized = this.onColumnResized.bind(this);
        this.editRow = this.editRow.bind(this);
        this.editTable = this.editTable.bind(this);
        this.checkRow = this.checkRow.bind(this);
        this.getSelectedCaseIds = this.getSelectedCaseIds.bind(this);
        this.editTableConfirm = this.editTableConfirm.bind(this);
        this.archiveSelected = this.archiveSelected.bind(this);
        this.archiveDialog = this.archiveDialog.bind(this);
        this.setCaseState = this.setCaseState.bind(this);
        this.downloadSelectedAsCsv = this.downloadSelectedAsCsv.bind(this);
        this.refreshIfNeeded = this.refreshIfNeeded.bind(this);
        this.onPagingChange = this.onPagingChange.bind(this);
        this.openQuickAction = this.openQuickAction.bind(this);
        this.closeQuickAction = this.closeQuickAction.bind(this);
        this.renderRowActions = this.renderRowActions.bind(this);
        this.editTableHeadersRef = React.createRef();
        this.state = {
            dialogTitle: '',
            dialogText: '',
            dialogConfirm: undefined,
            dialogOpen: false,
            dialogSize: undefined,
            dialogWarning: false,
            dialogPermanentlyDelete: false,
            loading: false,
            taskRows: undefined,
            checkedRows: {},
            importDialogOpen: false,
            quickAction: undefined,
            quickActionRow: undefined,
            quickActionLoading: false,
        };
    }


    componentDidMount() {
        this.refreshIfNeeded(this.props);
    }


    componentWillReceiveProps(nextProps) {
        // Rows appended by scrolling keep the current selection.
        const appended = this.props.rows && nextProps.rows && nextProps.rows.length > this.props.rows.length &&
            nextProps.rows[0] === this.props.rows[0];
        if (this.props.rows != nextProps.rows && !appended) {
            this.setState({checkedRows: {}});
        }

        this.refreshIfNeeded(nextProps);
    }

    onColumnResized(newHeaders) {
        this.props.dispatch(casesUpdateColumnsAction(newHeaders))
            .catch((error) => {
                console.error(new Error().stack, error);
                this.props.dispatch(caseSetServerError(error));
            });
    }

    editRow(rowIndex, currentCase) {
        navigateToCase(this.props.dispatch, currentCase.id, currentCase);
    }

    editTable() {
        this.setState({
            dialogTitle: 'Edit Columns',
            dialogText: <Well>Choose which columns should be visible and what should be their order. Hold CTRL (PC) / CMD (Mac) key when clicking to select multiple columns.</Well>,
            dialogSize: 'tall',
            dialogWarning: false,
            dialogConfirm: () => this.editTableConfirm(),
            dialogOpen: true,
            dialogChildren: 
                <EditTableHeaders activeHeaders={this.props.headers}
                                  workflow={this.props.workflow}
                                  ref={this.editTableHeadersRef}
                />
        });
    }

    archiveDialog() {
        this.setState({
            dialogTitle: 'Archive Cases',
            dialogText: 'Do you really want to archive the selected cases? Note that if the case is already archived, it will be unarchived.',
            dialogSize: undefined,
            dialogWarning: true,
            dialogConfirm: () => {
                this.archiveSelected(this.state.dialogPermanentlyDelete);
                this.setState({dialogOpen: false, checkedRows: {}});
            },
            dialogOpen: true,
            dialogPermanentlyDelete: false,
            dialogChildren:
                <Row>
                <br />
                <Col xs={12}>
                <CheckBox 
                    field={{
                        name: 'permanentlyDeleteCheckbox'
                    }}
                    label="Permanently delete"
                    help="If checked, the cases will be permanently deleted."
                    onChange={(_elem) => {
                        this.setState({dialogPermanentlyDelete: !this.state.dialogPermanentlyDelete});
                    }}
                />
                </Col>
                </Row>
        });
    }

    editTableConfirm() {
        const activeHeaders = this.editTableHeadersRef.current.getActiveHeaders();
        this.props.dispatch(casesUpdateColumnsAction(activeHeaders))
            .then(() => {
                this.setState({ dialogOpen: false });
            })
            .catch((error) => {
                console.error(new Error().stack, error);
                this.props.dispatch(caseSetServerError(error));
            });
    }

    checkRow(_rows, indexes) {
        let checkedRows;
        if (indexes === undefined) {
            checkedRows = {};
        } else {
            checkedRows = {...this.state.checkedRows};
            for (let i = 0; i < indexes.length; i++) {
                const index = indexes[i];
                checkedRows[index] = !checkedRows[index];
            }
        }

        this.setState({checkedRows});
    }

    getSelectedCaseIds() {
        const ids = [];
        if (!this.props.rows) {
            return ids;
        }

        for (const checked in this.state.checkedRows) {
            if (this.state.checkedRows[checked]) {
                const rowId = parseInt(checked, 10);
                ids.push(this.props.rows[rowId].id);
            }
        }

        return ids;
    }

    async archiveSelected(permanently) {
        this.setState({loading: true});
        const ids = this.getSelectedCaseIds();
        try {
            await this.props.dispatch(casesDeleteAction(ids, permanently))
        } catch (error) {
            console.error(new Error().stack, error);
            this.props.dispatch(caseSetServerError(error));
        }

        this.setState({loading: false});
    }

    async setCaseState(caseState) {
        this.setState({loading: true});
        const ids = this.getSelectedCaseIds();
        try {
            await this.props.dispatch(casesSetStatesAction(ids, caseState))
        } catch (error) {
            console.error(new Error().stack, error);
            this.props.dispatch(caseSetServerError(error));
        }

        this.setState({loading: false});
    }

    // Quick actions: load the selected case as the current case (the bare
    // minimum the reused forms need), then open the matching dialog.
    //
    openQuickAction(action, row) {
        // Changing the case state doesn't need the full case loaded.
        //
        if (action === 'state') {
            this.setState({ quickAction: 'state', quickActionRow: row });
            return;
        }

        this.setState({ quickActionLoading: true });
        this.props.dispatch(caseEditIdAction(row.id, row))
            .then(() => {
                this.setState({
                    quickAction: action,
                    quickActionRow: row,
                    quickActionLoading: false,
                });
            })
            .catch((error) => {
                console.error(new Error().stack, error);
                this.setState({ quickActionLoading: false });
                this.props.dispatch(caseSetServerError(error));
            });
    }

    closeQuickAction() {
        this.setState({ quickAction: undefined, quickActionRow: undefined });
    }

    renderRowActions(row) {
        return (
            <QuickActionsMenu
                row={row}
                onAction={this.openQuickAction}
            />
        );
    }

    async downloadSelectedAsCsv() {
        let { headers, rows } = this.props;
        const ids = this.getSelectedCaseIds();
        if (ids.length > 0) {
            // Selection is always within the loaded page.
            downloadCsv(headers, rows.filter(r => ids.includes(r.id)), 'cases.csv');
            return;
        }

        // Nothing selected: export every case matching the current search, not just this page.
        try {
            const { q, f, a } = this.props.query;
            const result = await request.get(loadAction('/api/v1/case/all', q, f, a), getClientAuthHeader());
            downloadCsv(headers, result.data.cases, 'cases.csv');
        } catch (error) {
            console.error(new Error().stack, error);
            this.props.dispatch(caseSetServerError(error));
        }
    }

    onPagingChange(paging) {
        this.props.dispatch(casesPagingAction(paging));
    }

    refreshIfNeeded(props) {
        if (props.refreshNeeded) {
            let query = props.query.q;
            let filter = props.query.f;
            let archived = props.query.a;

            // A new search or tile starts at the first page.
            let paging = props.paging;
            const querySignature = JSON.stringify([query, filter, archived]);
            if (this.querySignature !== undefined && this.querySignature !== querySignature && paging.page !== 0) {
                paging = { ...paging, page: 0, append: false };
            } else if (!paging.append && paging.page !== 0) {
                // A plain reload starts again from the first page; only scrolling appends.
                paging = { ...paging, page: 0 };
            }

            this.querySignature = querySignature;
            this.props.dispatch(casesLoadAction(query, filter, archived, paging))
                .then(() => {
                    this.setState({ taskRows: this.props.caseIds });
                })
                .catch((error) => {
                    console.error(new Error().stack, error);
                    this.props.dispatch(caseSetServerError(error));
                });
        } else {
            this.setState({ taskRows: props.rows && props.caseIds });
        }
    }

    render() {
        const {
            headers,
            rows,
            showDashboard = true,
            showTasks = true,
            recordType = 'case',
            rowClick = undefined,
            query = undefined,
            showSelectToolbar = true,
            states = [],
            paging,
            total,
        } = this.props;

        const loading = this.state.loading || this.props.loadingPage || typeof(this.props.rows) === 'undefined';
        const sortedColumn = paging.sort ? headers.findIndex(h => `${h.table}.${h.name}` === paging.sort) : -1;
        const size = findBootstrapEnvironment();
        const noCaseSelected = this.getSelectedCaseIds().length === 0;

        const header = (
            <div style={{ height: 25 }}>
                <h6 style={{ float: 'left', marginTop: 5 }}>{`Cases (${!rows ? 0 : total})`}</h6>
                {showSelectToolbar && (
                <div style={{ float: 'right' }}>
                <ButtonGroup style={{ boxShadow: 'none', marginTop: -8, marginRight: 0, marginBottom: -1, backgroundColor: 'rgba(0,0,0,0)' }}>
                    <OverlayTrigger
                        placement="bottom"
                        trigger={['hover', 'focus']}
                        overlay={<Tooltip id="tooltipCaseState">Set Case State</Tooltip>}
                    >
                        <DropdownButton
                            title={<Glyphicon glyph="th-large" style={{marginLeft: -3}}/>}
                            disabled={noCaseSelected}
                            id="bg-nested-dropdown"
                            key="bg-nested-dropdown"
                            style={{backgroundColor: 'rgba(0,0,0,0)', width: 45}}
                        >
                        {states.map((s, i) => <MenuItem eventKey={i} onClick={() => this.setCaseState(s['type'])}>{s['type']}</MenuItem>)}
                        </DropdownButton>
                    </OverlayTrigger>
                    <Button
                        onClick={this.archiveDialog}
                        disabled={noCaseSelected}
                        icon="inbox"
                        tooltip="Archive selected"
                        tooltipPosition="bottom"
                        style={{backgroundColor: 'rgba(0,0,0,0)'}}
                    />
                </ButtonGroup>
                <DropdownButton title={<Glyphicon glyph="option-vertical" />}
                                noCaret
                                pullRight
                                style={{ boxShadow: 'none', marginTop: -8, marginRight: -15, marginBottom: -1, backgroundColor: 'rgba(0,0,0,0)' }}
                >
                <MenuItem eventKey="downloadCsv" onClick={() => this.downloadSelectedAsCsv()}>Export to CSV</MenuItem>
                <MenuItem eventKey="uploadCsv" onClick={() => this.setState({ importDialogOpen: true })}>Import from CSV</MenuItem>
                </DropdownButton>
                </div>)}
            </div>);

        const output = (
            <div className="ct-page">
            {showDashboard && (
            <Row>
            <Col xs={12}>
                <Cards onClick={(state) => {
                           const route = filteredRoute('/case/all',
                                                       navigateToCases(this.props.query && this.props.query.hideFilterPanel,
                                                                       state));

                           browserHistory.push(route);
                       }}
                       cards={this.props.cards}
                       cardStats={this.props.cardStats}
                       cardColor={getCaseStateColor}
                       selectedCard={this.props.selectedCard}
                       id="tour-case-states-goto"
                />
            </Col>
            </Row>)}
            <Row>
                <Col xs={12} md={8} lg={8} id="casesTable" style={{ marginRight: 0 }}>
                    <Panel header={header}
                           data-hj-whitelist
                           style={{
                               marginTop: recordType === 'word' ? '-15px' : 0,
                               paddingBottom: recordType === 'word' ? '-15px' : 0,
                           }}
                    >
                    <Row>
                        <Col xs={12}>
                            <SearchForm {...this.props}
                                        recordType={recordType}
                                        hideAdvancedSearch={recordType === 'word'}
                                        query={query}
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col xs={12} id="tour-cases-table">
                            <Table headers={headers}
                                   rows={rows || []}
                                   customFieldsMap={
                                       {
                                           'payment.HourlyRate': {
                                               type: 'Cash'
                                           },
                                           'payment.Fee': {
                                               type: 'Cash'
                                           },
                                           'payment.FeeCents': {
                                               type: 'Cash'
                                            },
                                           'payment.FeeOwed': {
                                               type: 'Cash'
                                           },
                                           'payment.FeeUnpaid': {
                                                type: 'Cash'
                                            },
                                           'payment.FeePaid': {
                                               type: 'Cash'
                                           },
                                           'payment.ChargeAmount': {
                                               type: 'Cash'
                                           },
                                           'payment.ChargeAmountCents': {
                                            type: 'Cash'
                                        },
                                       }
                                   }
                                   onColumnResized={this.onColumnResized}
                                   editRow={rowClick || this.editRow}
                                   editBsStyle="link"
                                   editTable={this.editTable}
                                   checkRow={showSelectToolbar && this.checkRow}
                                   checkedRows={showSelectToolbar && this.state.checkedRows}
                                   renderRowActions={recordType === 'case' ? this.renderRowActions : undefined}
                                   buttonsWidth={20}
                                   filterable={false}
                                   loading={loading}
                                   defaultPageSize={10}
                                   serverPaging={{
                                       pages: Math.max(Math.ceil(total / paging.pageSize), 1),
                                       page: paging.page,
                                       pageSize: paging.pageSize,
                                       sorted: sortedColumn >= 0 ? [{ id: sortedColumn, desc: paging.desc }] : [],
                                       onChange: this.onPagingChange,
                                       loadingMore: !!paging.append && this.props.loadingPage,
                                   }}
                                   getTdProps={getSquareColorTdProps('action', (rowInfo, config) => getCaseStateColor(rowInfo && rowInfo.original && rowInfo.original['notes'] && rowInfo.original['notes']['CaseState'], config))}
                            />
                        </Col>
                    </Row>
                    </Panel>
                </Col>
                {showTasks && (
                <Col xs={12} md={4} lg={4}>
                    <Panel data-hj-whitelist header="Tasks" style={{ margin: 0 }}>
                        <TaskTable allTasks
                                   loading={loading && !paging.append}
                                   allTasksAllowedCaseIds={this.state.taskRows} />
                    </Panel>
                </Col>)}
            </Row>
            </div>);

        return (
            <React.Fragment>
                {output}
                <ConfirmDialog
                    title={this.state.dialogTitle}
                    warning={this.state.dialogWarning}
                    text={this.state.dialogText}
                    cancel={() => {this.setState({dialogOpen: false})}}
                    confirm={this.state.dialogConfirm}
                    open={this.state.dialogOpen}
                    size={this.state.dialogSize}
                >
                {this.state.dialogChildren}
                </ConfirmDialog>
                <ImportDialog
                    open={this.state.importDialogOpen}
                    cancel={() => this.setState({ importDialogOpen: false })}
                    title='Importing Cases'
                    type='case'
                    defaultValues={{'intake.CaseType': 'Traffic Infraction', 'notes.CaseState': 'Lead'}} // TODO!!
                />
                <CaseQuickActionDialogs
                    action={this.state.quickAction}
                    row={this.state.quickActionRow}
                    onClose={this.closeQuickAction}
                />
            </React.Fragment>
        );
    }
}

const mapStateToProps = (state, ownProps) => {
    let workflow;
    let filter = state.cases.filter;
    let rows = state.cases.rows;
    let refreshNeeded = state.cases.refreshNeeded;
    let headers = state.cases.headers;
    let config = state.user.config;
    let states = [];
    const query = ownProps.query || (ownProps.location && ownProps.location.query) || {};
    if (config) {
        if (!headers) {
            headers = [...config.caseHeaders];
        }

        workflow = [...config.workflow];
        states = state.user.config['caseStates'];
    }

    if (!headers) {
        headers = [];
    }

    const cards = (config && config.caseStates && config.caseStates.map(v => v.type)) || [];
    const cardStats = (state.user.stats && state.user.stats.caseStates) || {};
    Object.keys(cardStats).forEach((key) => {
        if (!cards.find(v => v === key)) {
            if (typeof(key) !== 'string') {
                key = JSON.stringify(key);
            }

            cards.push(key);
        }
    });

    // Null-safe: USER_LOG_OUT resets the whole store (incl. router state)
    // while this component may still be mounted for one render.
    const cardQuery = (state.routing.locationBeforeTransitions || {}).query || {};
    let selectedCard;
    if (cardQuery.hideFilterPanel !== 'false' && cardQuery.hideFilterPanel !== 'true') {
        selectedCard = cardQuery.hideFilterPanel;
    }

    return {
        headers,
        workflow,
        rows,
        total: state.cases.total,
        caseIds: state.cases.caseIds,
        paging: state.cases.paging,
        loadingPage: state.cases.loadingPage,
        filter,
        refreshNeeded,
        query,
        cards,
        cardStats,
        selectedCard,
        states
    };
};

export default connect(mapStateToProps)(windowSize(Cases));
